import React from "react";
import {
  Box,
  Text,
  Image,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Divider,
  useColorModeValue,
  VStack,
  Heading,
} from "@chakra-ui/react";
import ReactMarkdown from "react-markdown";
import eduviclogo from "../resources/Eduvictechlogo.png";
import remarkGfm from "remark-gfm";

export interface SimpleContent {
  type: "simple_content";
  content: string;
}

export interface TabbedContent {
  type: "tabbed_content";
  tabs: { title: string; content: string }[];
}

export type QuestionContext = SimpleContent | TabbedContent | null | undefined;

type TextSegment =
  | { kind: "text"; content: string }
  | { kind: "image"; url: string };

function parseTextWithImages(text: string): TextSegment[] {
  const segments: TextSegment[] = [];
  const regex = /\{\{image:([^}]+)\}\}/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ kind: "text", content: text.slice(lastIndex, match.index) });
    }
    segments.push({ kind: "image", url: match[1].trim() });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    segments.push({ kind: "text", content: text.slice(lastIndex) });
  }
  return segments;
}

// Chakra-compatible markdown component map -- keeps font sizes
// consistent with the rest of the UI rather than using browser
// defaults which would render headings at 2em etc.
const markdownComponents = (fontSize: string, fontWeight: string) => ({
  p: ({ children }: any) => (
    <Text fontSize={fontSize} mb={2} lineHeight="1.7">
      {children}
    </Text>
  ),
  strong: ({ children }: any) => (
    <Text as="span" fontWeight="bold" fontSize={fontSize}>
      {children}
    </Text>
  ),
  em: ({ children }: any) => (
    <Text as="span" fontStyle="italic" fontSize={fontSize}>
      {children}
    </Text>
  ),
  h1: ({ children }: any) => (
    <Text fontWeight="bold" fontSize="lg" mb={2} mt={3}>
      {children}
    </Text>
  ),
  h2: ({ children }: any) => (
    <Text fontWeight="bold" fontSize="md" mb={2} mt={3}>
      {children}
    </Text>
  ),
  h3: ({ children }: any) => (
    <Text fontWeight="semibold" fontSize={fontSize} mb={1} mt={2}>
      {children}
    </Text>
  ),
  ul: ({ children }: any) => (
    <Box as="ul" pl={5} mb={2} fontSize={fontSize}>
      {children}
    </Box>
  ),
  ol: ({ children }: any) => (
    <Box as="ol" pl={5} mb={2} fontSize={fontSize}>
      {children}
    </Box>
  ),
  li: ({ children }: any) => (
    <Box as="li" mb={1} fontSize={fontSize}>
      {children}
    </Box>
  ),
  blockquote: ({ children }: any) => (
    <Box
      borderLeft="3px solid"
      borderColor="blue.300"
      pl={3}
      py={1}
      my={2}
      bg="blue.50"
      borderRadius="sm"
    >
      <Text fontSize={fontSize} fontStyle="italic" color="gray.700">
        {children}
      </Text>
    </Box>
  ),
  hr: () => <Divider my={3} />,
  // Suppress default image handling -- images come through
  // {{image:...}} tags, not markdown image syntax
  img: () => null,

    table: ({ children }: any) => (
    <Box
      as="table"
      width="100%"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      overflow="hidden"
      my={3}
      fontSize={fontSize}
    >
      {children}
    </Box>
  ),

  thead: ({ children }: any) => (
    <Box as="thead" bg="gray.100">
      {children}
    </Box>
  ),

  tbody: ({ children }: any) => (
    <Box as="tbody">
      {children}
    </Box>
  ),

  tr: ({ children }: any) => (
    <Box
      as="tr"
      borderBottomWidth="1px"
      borderColor="gray.200"
    >
      {children}
    </Box>
  ),

  th: ({ children }: any) => (
    <Box
      as="th"
      px={3}
      py={2}
      textAlign="left"
      fontWeight="bold"
      fontSize={fontSize}
      color="gray.700"
    >
      {children}
    </Box>
  ),

  td: ({ children }: any) => (
    <Box
      as="td"
      px={3}
      py={2}
      fontSize={fontSize}
      borderTopWidth="1px"
      borderColor="gray.200"
    >
      {children}
    </Box>
  ),
});

// Renders a text segment that may contain markdown.
// Used for both question_text and question_context content.
function RenderMarkdownSegment({
  content,
  fontSize,
  fontWeight,
}: {
  content: string;
  fontSize: string;
  fontWeight: string;
}) {
  return (
    <Box>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={markdownComponents(fontSize, fontWeight) as any}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
}

// Public API -- unchanged signature so all call sites continue to work.
export function RenderTextWithImages({
  text,
  fontSize = "sm",
  fontWeight = "normal",
}: {
  text: string;
  fontSize?: string;
  fontWeight?: string;
}) {
  const segments = parseTextWithImages(text ?? "");

  return (
    <Box>
      {segments.map((seg, idx) => {
      if (seg.kind === "image") {
        return (
          <Box
            key={idx}
            position="relative"
            width="fit-content"
            maxW="100%"
            mx="auto"
            my={3}
          >
            {/* Question Image */}
            <Image
              src={seg.url}
              alt=""
              maxW="100%"
              maxH="400px"
              objectFit="contain"
              borderRadius="md"
              draggable={false}
            />

            {/* Watermark Overlay */}
            <Box
              position="absolute"
              inset={0}
              zIndex={2}
              pointerEvents="none"
              overflow="hidden"
              borderRadius="md"
            >
              {[...Array(12)].map((_, i) => (
                <Image
                  key={i}
                  src={eduviclogo}
                  alt=""
                  position="absolute"
                  width="120px"
                  opacity={0.25}
                  left={`${(i % 4) * 25}%`}
                  top={`${Math.floor(i / 4) * 30}%`}
                  transform="rotate(-30deg)"
                  filter="grayscale(100%) brightness(80%)"
                />
              ))}
            </Box>
          </Box>
        );
      }
        // Text segments now go through markdown renderer
        return (
          <RenderMarkdownSegment
            key={idx}
            content={seg.content}
            fontSize={fontSize}
            fontWeight={fontWeight}
          />
        );
      })}
    </Box>
  );
}



export function RenderQuestionContext({
  context,
  isMobile = false,
}: {
  context: QuestionContext;
  isMobile?: boolean;
}) {
  const tabBg = useColorModeValue("white", "gray.700");
  const headingColor = useColorModeValue("gray.700", "gray.200");

  if (!context) return null;

  if (context.type === "simple_content") {
    return (
      <RenderTextWithImages
        text={context.content}
        fontSize="sm"
        fontWeight="normal"
      />
    );
  }

  if (context.type === "tabbed_content") {
    // Mobile: no tab UI at all -- each tab becomes a stacked
    // headline + content + separator, read top-to-bottom.
    if (isMobile) {
      return (
        <VStack align="stretch" spacing={4}>
          {context.tabs.map((tab, idx) => (
            <Box key={idx}>
              <Heading size="sm" mb={2} color={headingColor}>
                {tab.title}
              </Heading>
              <RenderTextWithImages
                text={tab.content}
                fontSize="sm"
                fontWeight="normal"
              />
              {idx < context.tabs.length - 1 && (
                <Divider mt={4} borderColor="gray.300" />
              )}
            </Box>
          ))}
        </VStack>
      );
    }

    // Desktop: unchanged, real tabs
    return (
      <Tabs
        variant="enclosed-colored"
        size="sm"
        colorScheme="blue"
        isFitted
      >
        <TabList gap={2} mb={2}>
          {context.tabs.map((tab, idx) => (
            <Tab
              key={idx}
              fontSize="sm"
              fontWeight="semibold"
              borderRadius="md"
              _selected={{
                bg: "blue.500",
                color: "white",
                boxShadow: "md",
              }}
              _hover={{
                bg: "blue.100",
                color: "blue.700",
              }}
            >
              {tab.title}
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          {context.tabs.map((tab, idx) => (
            <TabPanel
              key={idx}
              bg={tabBg}
              borderRadius="md"
              boxShadow="sm"
            >
              <RenderTextWithImages
                text={tab.content}
                fontSize="sm"
                fontWeight="normal"
              />
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    );
  }

  return null;
}